import { MarkdownEditor } from "@hq/markdown-editor";
import { WhenPopover } from "./WhenPopover";
import { useClient, useServices, useTask } from "@hq/client-react";
import type { Task } from "@hq/contracts";
import { type NowContext, normalizeTags, todayOf } from "@hq/core";
import { Badge, Button, cn, IconButton, Input, Switch, TagInput, Textarea, toast } from "@z/ui";
import {
  CalendarClock,
  CalendarDays,
  ChevronDown,
  Flag,
  Folder,
  Inbox,
  MoreHorizontal,
  Trash2,
  X,
} from "lucide-react";
import {
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useState,
  useRef,
  useSyncExternalStore,
} from "react";
import {
  formatDueDate,
  formatDuration,
  formatScheduled,
  formatTimestamp,
  isOverdue,
} from "./format";
import {
  DueDatePopover,
  MoreMenu,
  PRIORITY_CLASS,
  PRIORITY_LABEL,
  PriorityMenu,
  ProjectMenu,
  SchedulePopover,
} from "./rowActions";
import { normalizeTag, useAllTags } from "./tags";
import { useNow } from "./useNow";
import { useProjectIndex } from "./useProjectNames";

export interface TaskDetailProps {
  taskId: string;
  source?: ReactNode;
  onClose?: () => void;
  /** Called after the task is deleted from this pane. */
  onDeleted?: () => void;
  /** Pins the clock; defaults to the shared ticking clock. */
  now?: NowContext;
  className?: string;
}

/** A field that edits a draft locally and commits on blur/Enter. Follows the task when it changes underneath. */
function useDraft(value: string): [string, (next: string) => void] {
  const [draft, setDraft] = useState(value);
  const [prev, setPrev] = useState(value);
  if (value !== prev) {
    // "Adjust state on prop change": the task was updated elsewhere (or committed); follow it.
    setPrev(value);
    setDraft(value);
  }
  return [draft, setDraft];
}

function TitleField({ task, onCommit }: { task: Task; onCommit: (title: string) => void }) {
  const [draft, setDraft] = useDraft(task.title);
  const cancelOnBlur = useRef(false);
  const commit = () => {
    if (cancelOnBlur.current) {
      cancelOnBlur.current = false;
      return;
    }
    const title = draft.trim();
    if (title && title !== task.title) onCommit(title);
    else setDraft(task.title);
  };
  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.nativeEvent.isComposing) {
      event.preventDefault();
      event.currentTarget.blur();
    } else if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      cancelOnBlur.current = true;
      setDraft(task.title);
      event.currentTarget.blur();
    }
  };
  return (
    <Textarea
      aria-label="Title"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={onKeyDown}
      className={cn(
        "field-sizing-content min-h-0 min-w-0 resize-none overflow-hidden border-transparent bg-transparent px-1 py-1 text-2xl font-semibold hover:border-border",
        task.status === "completed" && "text-fg-muted line-through",
      )}
    />
  );
}

function DescriptionField({ task }: { task: Task }) {
  const { taskAutosave } = useClient();
  const state = useSyncExternalStore(
    (notify) => taskAutosave.subscribe(task.id, notify),
    () => taskAutosave.getState(task.id),
  );
  useEffect(() => {
    const flush = () => void taskAutosave.flush(task.id);
    const onVisibility = () => {
      if (document.visibilityState === "hidden") flush();
    };
    window.addEventListener("pagehide", flush);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("pagehide", flush);
      document.removeEventListener("visibilitychange", onVisibility);
      flush();
    };
  }, [task.id, taskAutosave]);
  return (
    <section
      aria-label="Task description"
      className="min-w-0 rounded-lg border border-border/60 bg-bg/30"
    >
      <MarkdownEditor
        key={task.id}
        documentId={task.id}
        label="Description"
        placeholder="Add a description…"
        value={{ document: task.descriptionDocument, markdown: task.description ?? "" }}
        revision={task.revision}
        onChange={(content, baseRevision) => taskAutosave.update(task.id, content, baseRevision)}
        onBlur={() => void taskAutosave.flush(task.id)}
        acceptExternalChange={() => ["idle", "saved"].includes(taskAutosave.getState(task.id))}
        contentClassName="px-3 py-3 text-sm"
      />
      <div
        aria-live="polite"
        className="flex min-h-7 items-center justify-end px-3 pb-2 text-xs text-fg-faint"
      >
        {(state === "dirty" || state === "saving") && "Saving description…"}
        {state === "saved" && "Description saved locally"}
        {state === "error" && (
          <button className="text-danger" onClick={() => void taskAutosave.flush(task.id)}>
            Could not save · Retry
          </button>
        )}
      </div>
    </section>
  );
}

function NumberField({
  label,
  value,
  placeholder,
  onCommit,
}: {
  label: string;
  value: number | undefined;
  placeholder: string;
  onCommit: (minutes: number | null) => void;
}) {
  const [draft, setDraft] = useDraft(value === undefined ? "" : String(value));
  return (
    <Input
      type="number"
      min={1}
      inputMode="numeric"
      aria-label={label}
      placeholder={placeholder}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const n = Number.parseInt(draft, 10);
        const next = Number.isFinite(n) && n > 0 ? n : null;
        if (next !== (value ?? null)) onCommit(next);
      }}
      className="h-7 w-24"
    />
  );
}

function TextField({
  label,
  value,
  placeholder,
  onCommit,
}: {
  label: string;
  value: string | undefined;
  placeholder: string;
  onCommit: (text: string | null) => void;
}) {
  const [draft, setDraft] = useDraft(value ?? "");
  return (
    <Input
      aria-label={label}
      placeholder={placeholder}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => {
        const next = draft.trim() || null;
        if (next !== (value ?? null)) onCommit(next);
      }}
      className="h-7"
    />
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div
      data-slot="detail-row"
      className="grid grid-cols-[5.5rem_minmax(0,1fr)] items-center gap-3 py-2"
    >
      <span className="text-xs font-medium text-fg-muted">{label}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-2 text-sm [&>button]:max-w-full [&>button]:whitespace-normal">
        {children}
      </div>
    </div>
  );
}

const fieldButton =
  "h-7 justify-start gap-1.5 px-2 font-normal text-fg [&_svg]:text-fg-muted data-popup-open:bg-fg/6";

/**
 * A pane-sized editor for one task. Every control writes through a narrow service call; the
 * task itself is read from the live query. Description writes use serialized autosave.
 */
export function TaskDetail({
  taskId,
  source,
  onClose,
  onDeleted,
  now: nowOverride,
  className,
}: TaskDetailProps) {
  const services = useServices();
  const now = useNow(nowOverride);
  const { projects } = useProjectIndex();
  const allTags = useAllTags();
  const { data: task, isLoading } = useTask(taskId);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");

  const track = (p: Promise<unknown>) => {
    setStatus("saving");
    p.then(
      () => setStatus("saved"),
      () => setStatus("error"),
    );
  };

  if (!task) {
    return (
      <div className={cn("p-4 text-sm text-fg-muted", className)}>
        {isLoading ? "Loading…" : "This task no longer exists."}
      </div>
    );
  }

  const completed = task.status === "completed";
  const projectName =
    task.projectId !== undefined ? projects.find((p) => p.id === task.projectId)?.name : undefined;
  const overdue = task.dueDate !== undefined && !completed && isOverdue(task.dueDate, now);

  return (
    <div data-slot="task-detail" className={cn("flex min-w-0 flex-col gap-5 p-4", className)}>
      <div className="flex items-start gap-2">
        <Button
          variant={completed ? "secondary" : "outline"}
          size="sm"
          onClick={() => track(services.tasks.toggleComplete(task.id))}
        >
          {completed ? "Reopen" : "Complete"}
        </Button>
        <span
          data-slot="detail-status"
          aria-live="polite"
          className="ml-auto self-center text-xs text-fg-faint"
        >
          {status === "saving" && "Saving…"}
          {status === "saved" && "Saved locally"}
          {status === "error" && <span className="text-danger">Could not save</span>}
        </span>
        <MoreMenu
          task={task}
          {...(onDeleted ? { onDeleted } : {})}
          trigger={
            <IconButton label="More" size="sm">
              <MoreHorizontal />
            </IconButton>
          }
        />
        {onClose && (
          <IconButton label="Close" size="sm" onClick={onClose}>
            <X />
          </IconButton>
        )}
      </div>

      {source}
      <TitleField task={task} onCommit={(title) => track(services.tasks.rename(task.id, title))} />
      <DescriptionField key={task.id} task={task} />

      <div className="flex flex-col divide-y divide-border">
        <Row label="When">
          <WhenPopover
            task={task}
            now={now}
            trigger={
              <Button variant="ghost" size="sm" className={fieldButton}>
                <CalendarClock />
                {task.status === "someday"
                  ? "Someday"
                  : task.plannedDate
                    ? formatDueDate(task.plannedDate, now)
                    : task.scheduledAt
                      ? formatScheduled(task.scheduledAt, now)
                      : "Anytime"}
                {task.evening ? " · Evening" : ""}
                <ChevronDown className="ml-auto" />
              </Button>
            }
          />
        </Row>
        <Row label="Deadline">
          <DueDatePopover
            task={task}
            now={now}
            trigger={
              <Button variant="ghost" size="sm" className={fieldButton}>
                <CalendarDays />
                {task.dueDate !== undefined ? (
                  <span className={cn(overdue && "text-danger")}>
                    {formatDueDate(task.dueDate, now)}
                  </span>
                ) : (
                  <span className="text-fg-faint">Add deadline</span>
                )}
                <ChevronDown className="ml-auto" />
              </Button>
            }
          />
        </Row>
        <Row label="Time block">
          <SchedulePopover
            task={task}
            now={now}
            trigger={
              <Button variant="ghost" size="sm" className={fieldButton}>
                <CalendarClock />
                {task.scheduledAt !== undefined ? (
                  formatScheduled(task.scheduledAt, now)
                ) : (
                  <span className="text-fg-faint">Schedule a time</span>
                )}
                <ChevronDown className="ml-auto" />
              </Button>
            }
          />
        </Row>
        <Row label="Duration">
          {task.scheduledAt !== undefined ? (
            <NumberField
              label="Duration in minutes"
              value={task.scheduledDurationMinutes}
              placeholder="minutes"
              onCommit={(minutes) =>
                track(services.tasks.schedule(task.id, task.scheduledAt ?? null, minutes))
              }
            />
          ) : (
            <span className="text-xs text-fg-faint">Schedule a time first</span>
          )}
          {task.scheduledDurationMinutes !== undefined && (
            <span className="text-xs text-fg-faint">
              {formatDuration(task.scheduledDurationMinutes)}
            </span>
          )}
        </Row>
        <Row label="Priority">
          <PriorityMenu
            task={task}
            trigger={
              <Button variant="ghost" size="sm" className={fieldButton}>
                <Flag
                  className={
                    task.priority !== undefined ? PRIORITY_CLASS[task.priority] : undefined
                  }
                />
                {task.priority !== undefined ? (
                  PRIORITY_LABEL[task.priority]
                ) : (
                  <span className="text-fg-faint">None</span>
                )}
                <ChevronDown className="ml-auto" />
              </Button>
            }
          />
        </Row>
        <Row label="Project">
          <ProjectMenu
            task={task}
            projects={projects}
            trigger={
              <Button variant="ghost" size="sm" className={fieldButton}>
                {projectName !== undefined ? <Folder /> : <Inbox />}
                {projectName ?? "Inbox"}
                <ChevronDown className="ml-auto" />
              </Button>
            }
          />
        </Row>
        <Row label="Heading">
          <TextField
            label="Heading"
            value={task.heading}
            placeholder="Group inside the project"
            onCommit={(heading) => track(services.tasks.setHeading(task.id, heading))}
          />
        </Row>
        <Row label="Estimate">
          <NumberField
            label="Estimate in minutes"
            value={task.estimateMinutes}
            placeholder="minutes"
            onCommit={(minutes) => track(services.tasks.setEstimate(task.id, minutes))}
          />
          {task.estimateMinutes !== undefined && (
            <span className="text-xs text-fg-faint">{formatDuration(task.estimateMinutes)}</span>
          )}
        </Row>
        <Row label="Tags">
          <TagInput
            aria-label="Tags"
            size="sm"
            placeholder="Add a tag…"
            value={task.tags ?? []}
            suggestions={allTags.map((t) => t.tag)}
            normalize={normalizeTag}
            onChange={(next) => track(services.tasks.setTags(task.id, normalizeTags(next)))}
          />
        </Row>
        <Row label="Evening">
          <Switch
            aria-label="This evening"
            checked={!!task.evening}
            onCheckedChange={(checked) =>
              track(
                checked
                  ? services.tasks
                      .plan(task.id, todayOf(now))
                      .then(() => services.tasks.setEvening(task.id, true))
                  : services.tasks.setEvening(task.id, false),
              )
            }
          />
        </Row>
        <Row label="Someday">
          <Switch
            aria-label="Someday"
            checked={task.status === "someday"}
            onCheckedChange={(checked) =>
              track(services.tasks.setStatus(task.id, checked ? "someday" : "open"))
            }
          />
          {task.status === "someday" && <Badge variant="outline">Parked</Badge>}
        </Row>
      </div>

      <div className="mt-2 flex flex-col gap-0.5 text-xs text-fg-faint">
        <span>Created {formatTimestamp(task.createdAt, now.timeZone)}</span>
        <span>Updated {formatTimestamp(task.updatedAt, now.timeZone)}</span>
        {task.completedAt !== undefined && (
          <span>Completed {formatTimestamp(task.completedAt, now.timeZone)}</span>
        )}
      </div>

      <div className="mt-auto pt-2">
        <Button
          variant="ghost"
          size="sm"
          className="text-danger hover:bg-danger/10 hover:text-danger"
          onClick={() => {
            void services.tasks
              .delete(task.id)
              .then(() => {
                onDeleted?.();
                toast({
                  title: `Moved "${task.title}" to Trash`,
                  action: { label: "Undo", onClick: () => void services.tasks.restore(task.id) },
                });
              })
              .catch(() => toast({ title: "Could not delete task", variant: "danger" }));
          }}
        >
          <Trash2 />
          Delete task
        </Button>
      </div>
    </div>
  );
}
